<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import SalesAPI from 'dashboard/api/sales';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');

const isAdmin = computed(
  () => currentUser.value?.role === 'administrator'
);

const sales = ref([]);
const dashboard = ref(null);
const isLoading = ref(false);
const selectedPeriod = ref('month');

const brl = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value ?? 0
  );

const formatDate = iso => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const periods = [
  { key: 'today', label: t('SALES.REPORT.PERIOD_TODAY') },
  { key: 'week', label: t('SALES.REPORT.PERIOD_WEEK') },
  { key: 'month', label: t('SALES.REPORT.PERIOD_MONTH') },
];

const periodParams = computed(() => {
  const now = new Date();
  let since;
  if (selectedPeriod.value === 'today') {
    since = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000;
  } else if (selectedPeriod.value === 'week') {
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    since = new Date(now.getFullYear(), now.getMonth(), diff).getTime() / 1000;
  } else {
    since = new Date(now.getFullYear(), now.getMonth(), 1).getTime() / 1000;
  }
  return { since: Math.floor(since), until: Math.floor(Date.now() / 1000) };
});

const totalAmount = computed(() =>
  sales.value.filter(s => s.status === 'active').reduce((sum, s) => sum + s.amount, 0)
);
const totalCount = computed(() => sales.value.filter(s => s.status === 'active').length);
const cancelledCount = computed(() => sales.value.filter(s => s.status === 'cancelled').length);

const load = async () => {
  isLoading.value = true;
  try {
    const [salesRes, dashRes] = await Promise.all([
      SalesAPI.get(periodParams.value),
      isAdmin.value ? SalesAPI.dashboard(periodParams.value) : Promise.resolve({ data: null }),
    ]);
    sales.value = salesRes.data;
    dashboard.value = dashRes.data;
  } catch {
    sales.value = [];
    dashboard.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch(selectedPeriod, load);
onMounted(load);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <!-- Header -->
    <header class="sticky top-0 z-10 px-6 bg-n-surface-1 border-b border-n-weak">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-16 gap-4">
          <h1 class="text-xl font-medium text-n-slate-12">
            {{ t('SALES.REPORT.TITLE') }}
          </h1>
          <div class="flex rounded-lg border border-n-strong overflow-hidden">
            <button
              v-for="p in periods"
              :key="p.key"
              class="px-3 py-1.5 text-xs font-medium transition-colors"
              :class="
                selectedPeriod === p.key
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="selectedPeriod = p.key"
            >
              {{ p.label }}
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Content -->
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-6">

        <!-- Loading -->
        <div v-if="isLoading" class="flex justify-center py-20">
          <span class="i-lucide-loader-circle animate-spin text-3xl text-n-slate-9" />
        </div>

        <template v-else>
          <!-- Summary cards -->
          <div class="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center">
              <p class="text-3xl font-bold text-s-green-600">{{ totalCount }}</p>
              <p class="text-xs text-n-slate-10 mt-1.5">{{ t('SALES.REPORT.TOTAL_SALES') }}</p>
            </div>
            <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center">
              <p class="text-2xl font-bold text-n-slate-12">{{ brl(totalAmount) }}</p>
              <p class="text-xs text-n-slate-10 mt-1.5">{{ t('SALES.REPORT.TOTAL_AMOUNT') }}</p>
            </div>
            <div class="rounded-xl border border-n-strong bg-n-alpha-3 p-5 text-center">
              <p class="text-3xl font-bold text-s-red-500">{{ cancelledCount }}</p>
              <p class="text-xs text-n-slate-10 mt-1.5">{{ t('SALES.REPORT.CANCELLED') }}</p>
            </div>
          </div>

          <!-- Manager: agent breakdown -->
          <div v-if="isAdmin && dashboard?.team?.agents?.length" class="mb-6">
            <h2 class="text-sm font-semibold text-n-slate-12 mb-3">
              {{ t('SALES.REPORT.BY_AGENT') }}
            </h2>
            <div class="rounded-xl border border-n-strong overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-n-alpha-3">
                  <tr>
                    <th class="text-left px-4 py-2.5 text-xs font-medium text-n-slate-10">{{ t('SALES.REPORT.AGENT') }}</th>
                    <th class="text-right px-4 py-2.5 text-xs font-medium text-n-slate-10">{{ t('SALES.REPORT.SALES_COUNT') }}</th>
                    <th class="text-right px-4 py-2.5 text-xs font-medium text-n-slate-10">{{ t('SALES.REPORT.AMOUNT') }}</th>
                    <th class="text-right px-4 py-2.5 text-xs font-medium text-n-slate-10">{{ t('SALES.REPORT.GOAL') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="agent in dashboard.team.agents"
                    :key="agent.agent_id"
                    class="border-t border-n-weak hover:bg-n-alpha-2 transition-colors"
                  >
                    <td class="px-4 py-3 text-n-slate-12 font-medium">{{ agent.agent_name }}</td>
                    <td class="px-4 py-3 text-right text-n-slate-11">{{ agent.total_with_sale }}</td>
                    <td class="px-4 py-3 text-right font-medium text-s-green-600">{{ brl(agent.total_amount) }}</td>
                    <td class="px-4 py-3 text-right">
                      <span
                        v-if="agent.goal"
                        class="text-xs px-2 py-0.5 rounded-full font-medium text-white"
                        :class="{
                          'bg-s-green-500': agent.goal.status === 'met',
                          'bg-s-yellow-500': agent.goal.status === 'in_progress',
                          'bg-s-red-500': agent.goal.status === 'not_met',
                        }"
                      >
                        {{ agent.goal.percentage }}%
                      </span>
                      <span v-else class="text-xs text-n-slate-9">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Sales list -->
          <h2 class="text-sm font-semibold text-n-slate-12 mb-3">
            {{ t('SALES.REPORT.SALES_LIST') }}
          </h2>
          <div v-if="sales.length" class="rounded-xl border border-n-strong overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-n-alpha-3">
                <tr>
                  <th class="text-left px-4 py-2.5 text-xs font-medium text-n-slate-10">{{ t('SALES.REPORT.DATE') }}</th>
                  <th class="text-left px-4 py-2.5 text-xs font-medium text-n-slate-10">{{ t('SALES.REPORT.ORDER') }}</th>
                  <th v-if="isAdmin" class="text-left px-4 py-2.5 text-xs font-medium text-n-slate-10">{{ t('SALES.REPORT.AGENT') }}</th>
                  <th class="text-left px-4 py-2.5 text-xs font-medium text-n-slate-10">{{ t('SALES.REPORT.TYPE') }}</th>
                  <th class="text-right px-4 py-2.5 text-xs font-medium text-n-slate-10">{{ t('SALES.REPORT.AMOUNT') }}</th>
                  <th class="text-center px-4 py-2.5 text-xs font-medium text-n-slate-10">{{ t('SALES.REPORT.STATUS') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="sale in sales"
                  :key="sale.id"
                  class="border-t border-n-weak hover:bg-n-alpha-2 transition-colors"
                >
                  <td class="px-4 py-3 text-n-slate-10 text-xs">{{ formatDate(sale.created_at) }}</td>
                  <td class="px-4 py-3 text-n-slate-12 font-mono text-xs">{{ sale.order_number }}</td>
                  <td v-if="isAdmin" class="px-4 py-3 text-n-slate-11">{{ sale.agent_name }}</td>
                  <td class="px-4 py-3 text-n-slate-10 text-xs">
                    <span v-if="sale.sale_type" class="flex items-center gap-1">
                      <span class="i-lucide-tag text-[10px]" />
                      {{ sale.sale_type }}
                    </span>
                    <span v-else class="text-n-slate-8">—</span>
                  </td>
                  <td
                    class="px-4 py-3 text-right font-medium"
                    :class="sale.status === 'cancelled' ? 'text-n-slate-9 line-through' : 'text-s-green-600'"
                  >
                    {{ brl(sale.amount) }}
                  </td>
                  <td class="px-4 py-3 text-center">
                    <span
                      class="text-xs px-2 py-0.5 rounded-full font-medium"
                      :class="
                        sale.status === 'active'
                          ? 'bg-s-green-100 text-s-green-700'
                          : 'bg-s-red-100 text-s-red-700'
                      "
                    >
                      {{
                        sale.status === 'active'
                          ? t('SALES.INFO_PANEL.STATUS_ACTIVE')
                          : t('SALES.INFO_PANEL.STATUS_CANCELLED')
                      }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-sm text-n-slate-9 text-center py-10">
            {{ t('SALES.REPORT.EMPTY') }}
          </p>
        </template>
      </div>
    </main>
  </section>
</template>
